import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { State } from 'model/State';
import { selectAccountHistory, selectTrackedElsewhere } from 'modules/mac/duck/selectors';
import { Dimensions } from 'components/visuals';
import {
  StyledButtonsWrapper,
  StyledFlex,
  StyledSenInfoWrapper,
  StyledSpan,
  StyledTableData,
  StyledTDSection,
  StyledTextLabel,
  StyledTextValueWrapper,
} from '../licensesExpandedRow/StyledComponents';
import { messages } from '../../messages';
import { fetchPartnerDetails } from 'modules/mac/duck/actions/contact';
import AccountHistory from '../licensesExpandedRow/licensesRowHistory';
import { ActionsMessage } from '../licensesExpandedRow/ActionsSectionElements/ActionsMessage';
import { ArchiveUnarchiveButtonLink } from '../licensesExpandedRow/ActionsSectionElements/ArchiveUnarchiveButtonLink';
import { OrganisationData } from 'modules/mac/common/types';
import t from 'modules/i18n/intl';
import { SubscriptionHasBeenMovedMessage } from './SubscriptionHasBeenMovedMessage';
import { CloudMigrationTrialOfferBanner } from './CloudMigrationTrialOfferBanner';

export const DetailsRow = ({ label, value, dataCy = '', title = '' }) => {
  return (
    <StyledFlex {...(dataCy ? { 'data-cy': dataCy } : {})} width={`${Dimensions.units(12.5)}%`}>
      <StyledTextLabel title={title}>{label}</StyledTextLabel>
      <StyledTextValueWrapper>
        <StyledSpan padding={`0 ${Dimensions.units(1.25)}px`}>{value}</StyledSpan>
      </StyledTextValueWrapper>
    </StyledFlex>
  );
};

type PropTypes = {
  accountDetails: any;
  canUserSeeAccountDetails: boolean;
  technicalContact: OrganisationData;
};

const DetailsRowContent: React.FC<PropTypes> = ({
  accountDetails,
  canUserSeeAccountDetails,
  technicalContact,
}) => {
  const dispatch = useDispatch();
  const isTrackedElsewhere: boolean = useSelector((state) =>
    selectTrackedElsewhere(state as State, accountDetails.id),
  );

  const accountHistory: any = useSelector((state) =>
    selectAccountHistory(state as State, accountDetails.id),
  );

  useEffect(() => {
    accountHistory?.periods.forEach((period) => {
      if (period.partnerId) dispatch(fetchPartnerDetails(period.partnerId));
    });
  }, [accountHistory]);

  const { id: accountId } = accountDetails;

  if (!accountDetails) return null;

  return canUserSeeAccountDetails ? (
    <>
      <StyledTableData paddingLeft colSpan={4}>
        <StyledTDSection>
          {accountDetails.entSubMigrated && (
            <SubscriptionHasBeenMovedMessage
              transactionAccountId={accountDetails.transactionAccountId}
              externalUuid={accountDetails.externalUuid}
            />
          )}
          <StyledSenInfoWrapper>
            <DetailsRow
              title={t('change.search.label.sen.title')}
              label={<FormattedMessage {...messages.name} />}
              value={accountDetails?.name}
            />
            <DetailsRow
              label={<FormattedMessage {...messages.SEN} />}
              value={accountDetails?.id ? `SEN-${accountDetails.id}` : ''}
              dataCy="mac-sen-details"
            />
            <DetailsRow
              label={<FormattedMessage {...messages.technicalContact} />}
              value={
                <p>
                  {technicalContact?.contactDetails.displayName}
                  <br />
                  {technicalContact?.contactDetails.phone}
                  <br />
                  {technicalContact?.contactDetails.email}
                  <br />
                  {technicalContact?.organisationDetails.name}
                </p>
              }
              dataCy="mac-sen-details"
            />
          </StyledSenInfoWrapper>
          {!accountDetails.entSubMigrated && (
            <CloudMigrationTrialOfferBanner account={accountDetails} />
          )}
        </StyledTDSection>
      </StyledTableData>
      <StyledTableData colSpan={2}>
        {!isTrackedElsewhere && (
          <>
            <StyledTDSection>
              <>
                <StyledFlex>
                  <StyledTextLabel width={`${Dimensions.units(3.4)}%`}>
                    <FormattedMessage {...messages.organizationDetails} />
                  </StyledTextLabel>
                  {technicalContact?.organisationDetails.name}
                </StyledFlex>
                {accountDetails?.license && (
                  <StyledFlex>
                    <StyledTextLabel width={`${Dimensions.units(3.4)}%`}>
                      <FormattedMessage {...messages.licenseKey} />
                    </StyledTextLabel>
                    <textarea readOnly style={{ resize: 'vertical' }} rows={5}>
                      {accountDetails?.license}
                    </textarea>
                  </StyledFlex>
                )}
                <StyledFlex>
                  <ActionsMessage />
                  <StyledButtonsWrapper>
                    <ArchiveUnarchiveButtonLink
                      isAccount
                      isArchived={false}
                      accountId={accountId}
                      isArchiveButtonVisible
                    />
                  </StyledButtonsWrapper>
                </StyledFlex>
              </>
              <StyledFlex>
                <StyledTextLabel width={`${Dimensions.units(3.4)}%`}>
                  <FormattedMessage {...messages.history} />
                </StyledTextLabel>
                <AccountHistory accountHistory={accountHistory} />
              </StyledFlex>
            </StyledTDSection>
          </>
        )}
      </StyledTableData>
    </>
  ) : (
    // Show the proper error message once we have it: https://hello.jira.atlassian.cloud/browse/CHT-2819
    <StyledTableData paddingLeft colSpan={6}>
      <p>You don&apos;t have permissions to see the details</p>
    </StyledTableData>
  );
};

export default DetailsRowContent;
